import { cwpNav } from './nav';
import { cwpReviewTabs } from './reviewTabs';
import { cwpStickyCta } from './stickyCta';
import { cwpSlotDemo } from './slotDemo';
import { cwpTooltip } from './tooltip';

export const cwpCasino = {
	init() {
		this.initLoadMore();
		this.initCardCarousels();
		this.initTableOfContents();
		this.initToplistFilters();
		cwpNav.init();
		cwpReviewTabs.init();
		cwpStickyCta.init();
		cwpSlotDemo.init();
		cwpTooltip.init();
	},

	initToplistFilters() {
		// Bind to any cwp-casino-toplist that opted into filters by emitting
		// [data-toplist-list] on its inner container (guide + stacked do this).
		document
			.querySelectorAll('.cwp-casino-toplist:has([data-toplist-list])')
			.forEach((root) => {
				const list = root.querySelector('[data-toplist-list]');

				if (!list) {
					return;
				}

				// Cards live as .cwp-guide-card (guide layout), .cwp-stack
				// (stacked layout) or .cwp-verdict-row (compact layout). All
				// carry the same data-* attributes for filter + sort.
				const cardSelector =
					'.cwp-guide-card, .cwp-stack, .cwp-verdict-row, .cwp-offer-card2';

				const getItems = () =>
					Array.from(list.querySelectorAll('.cwp-toplist__item'));

				const apply = () => {
					const mode = sort ? sort.value : 'recommended';
					const items = getItems();

					const visible = items.filter((item) => {
						const card = item.querySelector(cardSelector);

						if (!card) {
							return false;
						}

						if (activeFilter !== 'all') {
							const cats = (
								card.getAttribute('data-categories') || ''
							).split(',');

							if (!cats.includes(activeFilter)) {
								return false;
							}
						}

						return !item.classList.contains(
							'cwp-toplist__item--hidden'
						);
					});

					const sorted = [...visible].sort((a, b) => {
						const ca = a.querySelector(cardSelector);
						const cb = b.querySelector(cardSelector);

						if (!ca || !cb) {
							return 0;
						}

						const num = (el, key) =>
							parseFloat(el.getAttribute(`data-${key}`) || '0');

						switch (mode) {
							case 'rating':
								return num(cb, 'rating') - num(ca, 'rating');
							case 'bonus':
								return num(cb, 'bonus') - num(ca, 'bonus');
							case 'freespins':
								return (
									num(cb, 'freespins') - num(ca, 'freespins')
								);
							case 'wagering':
								return (
									num(ca, 'wagering') - num(cb, 'wagering')
								);
							default:
								return num(ca, 'order') - num(cb, 'order');
						}
					});

					items.forEach((item) => item.style.display = 'none');
					sorted.forEach((item) => {
						item.style.display = '';
						list.appendChild(item);
					});
				};

				const pills = root.querySelectorAll('[data-filter]');
				const sort = root.querySelector('[data-toplist-sort]');
				let activeFilter = 'all';

				pills.forEach((pill) => {
					pill.addEventListener('click', () => {
						activeFilter = pill.getAttribute('data-filter') || 'all';

						pills.forEach((p) => {
							const active = p === pill;
							p.classList.toggle('is-active', active);
							p.setAttribute(
								'aria-selected',
								active ? 'true' : 'false'
							);
						});

						apply();
					});
				});

				if (sort) {
					sort.addEventListener('change', apply);
				}
			});
	},

	initCardCarousels() {
		document.querySelectorAll('.cwp-casino-cards').forEach((root) => {
			const scroller = root.querySelector('[data-cards-scroller]');

			if (!scroller) {
				return;
			}

			const nav = root.querySelector('[data-cards-nav]');
			const prev = root.querySelector('[data-cards-prev]');
			const next = root.querySelector('[data-cards-next]');
			const dotsWrap = root.querySelector('[data-cards-dots]');

			const isScrollable = () =>
				scroller.scrollWidth - scroller.clientWidth > 2;

			const step = () => {
				const item = scroller.querySelector('.cwp-toplist__item');

				if (!item) {
					return scroller.clientWidth;
				}

				const styles = getComputedStyle(scroller);
				const gap =
					parseFloat(styles.columnGap || styles.gap || '0') || 0;
				const itemWidth = item.getBoundingClientRect().width + gap;
				const perView = Math.max(
					1,
					Math.round(scroller.clientWidth / itemWidth)
				);

				return itemWidth * perView;
			};

			const pageCount = () => {
				const view = scroller.clientWidth;

				if (view <= 0) {
					return 1;
				}

				return Math.max(
					1,
					Math.ceil((scroller.scrollWidth - view) / view) + 1
				);
			};

			const currentPage = () => {
				const view = scroller.clientWidth;

				return view <= 0 ? 0 : Math.round(scroller.scrollLeft / view);
			};

			const buildDots = () => {
				if (!dotsWrap) {
					return;
				}

				dotsWrap.innerHTML = '';
				const count = pageCount();

				if (count <= 1) {
					return;
				}

				for (let i = 0; i < count; i += 1) {
					const dot = document.createElement('button');
					dot.type = 'button';
					dot.className = 'cwp-casino-cards__dot';
					dot.setAttribute('role', 'tab');
					dot.setAttribute('aria-label', String(i + 1));
					dot.addEventListener('click', () => {
						scroller.scrollTo({
							left: i * scroller.clientWidth,
							behavior: 'smooth',
						});
					});
					dotsWrap.appendChild(dot);
				}
			};

			const sync = () => {
				const scrollable = isScrollable();

				if (nav) {
					nav.hidden = !scrollable;
				}

				if (!scrollable) {
					return;
				}

				const max = scroller.scrollWidth - scroller.clientWidth;

				if (prev) {
					prev.disabled = scroller.scrollLeft <= 1;
				}

				if (next) {
					next.disabled = scroller.scrollLeft >= max - 1;
				}

				if (dotsWrap) {
					const active = currentPage();

					Array.from(dotsWrap.children).forEach((dot, i) => {
						dot.setAttribute(
							'aria-selected',
							i === active ? 'true' : 'false'
						);
					});
				}
			};

			if (prev) {
				prev.addEventListener('click', () =>
					scroller.scrollBy({ left: -step(), behavior: 'smooth' })
				);
			}

			if (next) {
				next.addEventListener('click', () =>
					scroller.scrollBy({ left: step(), behavior: 'smooth' })
				);
			}

			let scrollRaf = null;
			scroller.addEventListener(
				'scroll',
				() => {
					if (scrollRaf) {
						return;
					}

					scrollRaf = requestAnimationFrame(() => {
						scrollRaf = null;
						sync();
					});
				},
				{ passive: true }
			);

			let resizeTimer = null;
			window.addEventListener('resize', () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					buildDots();
					sync();
				}, 150);
			});

			buildDots();
			sync();
		});
	},

	initLoadMore() {
		document.querySelectorAll('.cwp-load-more').forEach((button) => {
			button.addEventListener('click', () => {
				const wrapper = button.closest('.cwp-casino-toplist');
				if (!wrapper) {
					return;
				}

				wrapper
					.querySelectorAll('.cwp-toplist__item--hidden')
					.forEach((item) => {
						item.classList.remove('cwp-toplist__item--hidden', 'hidden');
					});

				button.parentElement
					? button.parentElement.remove()
					: button.remove();
			});
		});
	},

	initTableOfContents() {
		const tocs = document.querySelectorAll('[data-toc]');

		if (!tocs.length) {
			return;
		}

		const scope =
			document.querySelector('.entry-content') ||
			document.querySelector('main') ||
			document.querySelector('article') ||
			document.body;

		tocs.forEach((toc) => {
			const list = toc.querySelector('.cwp-toc__list');
			if (!list) {
				return;
			}

			const includeH3 = toc.getAttribute('data-include-h3') === '1';
			const selector = includeH3 ? 'h2, h3' : 'h2';
			const headings = Array.from(scope.querySelectorAll(selector)).filter(
				(heading) => !toc.contains(heading) && heading.textContent.trim() !== ''
			);

			if (!headings.length) {
				return;
			}

			const used = {};

			headings.forEach((heading) => {
				if (!heading.id) {
					heading.id = uniqueSlug(heading.textContent, used);
				} else {
					used[heading.id] = true;
				}

				const li = document.createElement('li');
				if (heading.tagName.toLowerCase() === 'h3') {
					li.className = 'pl-4';
				}

				const link = document.createElement('a');
				link.href = `#${heading.id}`;
				link.textContent = heading.textContent.trim();
				link.className =
					'font-medium text-[var(--wp--custom--cwp--brand)] hover:text-[var(--wp--custom--cwp--brand-strong)] transition-colors';

				li.appendChild(link);
				list.appendChild(li);
			});

			toc.removeAttribute('hidden');
		});
	},
};

function uniqueSlug(text, used) {
	let base = text
		.trim()
		.toLowerCase()
		.replace(/[^\w\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-');

	if (!base) {
		base = 'section';
	}

	let slug = base;
	let i = 2;

	while (used[slug]) {
		slug = `${base}-${i}`;
		i += 1;
	}

	used[slug] = true;
	return slug;
}
